import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTenant } from '../context/TenantContext';
import { getProjects } from '../services/projectService';

/**
 * TenantDashboardPage — community overview for the /t/:tenantSlug/dashboard route.
 *
 * Accessible to platform staff (viewing any community) and active community members.
 * Data comes from TenantContext (viewTenant) rather than AuthContext.activeTenant.
 */
const TenantDashboardPage = () => {
  const { tenantSlug } = useParams();
  const { viewTenant, viewerRole, isPlatformStaff, isViewModuleEnabled } = useTenant();

  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (viewTenant?._id) {
      getProjects(viewTenant._id)
        .then((res) => setProjects(res.data))
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [viewTenant]);

  const displayName = viewTenant?.branding?.displayName || viewTenant?.name || tenantSlug;
  const primaryColor = viewTenant?.branding?.primaryColor || '#0ea5e9';

  // Module tiles for quick navigation
  const moduleTiles = [
    isViewModuleEnabled('consultation')  && { to: `/t/${tenantSlug}/consultation`,   label: 'Consultation',    icon: '💬' },
    isViewModuleEnabled('communityNews') && { to: `/t/${tenantSlug}/community-news`, label: 'Community News',  icon: '📰' },
    isViewModuleEnabled('communityData') && { to: `/t/${tenantSlug}/community-data`, label: 'Community Data',  icon: '📊' },
    isViewModuleEnabled('harvesting')    && { to: `/t/${tenantSlug}/harvesting`,     label: 'Harvesting',      icon: '🌿' },
    isViewModuleEnabled('landValues')    && { to: `/t/${tenantSlug}/land-values`,    label: 'Land Values',     icon: '🗺️' },
    isViewModuleEnabled('landUnits')     && { to: `/t/${tenantSlug}/land-units`,     label: 'Land Units',      icon: '🗻' },
  ].filter(Boolean);

  return (
    <div className="page dashboard-page">
      <div className="page-header">
        <h1 style={{ borderLeft: `4px solid ${primaryColor}`, paddingLeft: 12 }}>
          {displayName}
        </h1>
        {viewTenant?.description && (
          <p style={{ color: '#94a3b8', marginTop: 4 }}>{viewTenant.description}</p>
        )}
        {isPlatformStaff && viewerRole && (
          <span className="badge badge-gray" style={{ fontSize: 11, marginTop: 8 }}>
            Viewing as {viewerRole}
          </span>
        )}
      </div>

      {/* Module quick-links */}
      {moduleTiles.length > 0 && (
        <section style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, marginBottom: 10 }}>
            MODULES
          </h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {moduleTiles.map(({ to, label, icon }) => (
              <Link
                key={to}
                to={to}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#0f172a',
                  border: '1px solid #1e293b',
                  borderRadius: 8,
                  padding: '8px 16px',
                  color: '#e2e8f0',
                  textDecoration: 'none',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {icon} {label}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Projects */}
      <section>
        <div className="section-header">
          <h2>Projects</h2>
        </div>
        {loading ? (
          <p>Loading…</p>
        ) : projects.length === 0 ? (
          <p style={{ color: '#64748b', fontStyle: 'italic' }}>No projects yet for this community.</p>
        ) : (
          <ul className="item-list">
            {projects.map((p) => (
              <li key={p._id}>
                <Link to={`/projects/${p._id}`}>{p.title}</Link>
                <span className="badge">{p.type}</span>
                <span className="badge badge-gray">{p.status}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default TenantDashboardPage;
